import { useMcpTool } from 'webmcp-react'
import { uiStore } from 'shared/store/uiStore'
import { InboxesApi } from 'entities/Inbox'
import { BroadcastsRoutes } from 'entities/ExportRouters'
import {
  listBroadcastNumbersSchema,
  openBroadcastCreateSchema,
} from 'entities/WebMcp/model/schemas'
import { okResult, errorResult } from 'entities/WebMcp/lib/toolResult'
import { waitForToolRegistered } from 'entities/WebMcp/lib/waitForTool'

/**
 * Registers the global broadcast tools. Renders nothing.
 * - list_broadcast_numbers (read): eligible sending numbers, from the same source
 *   and filter as the broadcast form's "Send from" picker.
 * - open_broadcast_create (navigation): opens the broadcast create form, which
 *   then registers the set_broadcast_* fill tools.
 *
 * The form itself is filled through page-scoped fill tools (set_broadcast_*)
 * registered by BroadcastFillTools when the create/edit form is open. See the
 * WebMCP contextual-fill-tools design.
 */
export const BroadcastTools = () => {
  useMcpTool({
    name: 'open_broadcast_create',
    description:
      'Open the broadcast create form (Outbound). Once open, the form advertises set_broadcast_details, set_broadcast_audience, and set_broadcast_sender; re-check the available tools after opening it.',
    input: openBroadcastCreateSchema,
    annotations: { readOnlyHint: true },
    handler: async () => {
      try {
        uiStore.changeRouteViaVue({
          path: `/${BroadcastsRoutes.root}/${BroadcastsRoutes.create}`,
        })
        // Resolve only once the form has mounted and registered its fill tools, so
        // the agent's next turn sees them and does not call them before they exist.
        const ready = await waitForToolRegistered('set_broadcast_details', 6000)
        return okResult({
          opened: true,
          form_ready: ready,
          next: ready
            ? 'The form is open. Re-check available tools, then fill it with set_broadcast_details, set_broadcast_audience, and set_broadcast_sender.'
            : 'The form is opening but its fill tools are not registered yet. Re-check available tools; if the set_broadcast_* tools are still missing, call open_broadcast_create again before trying to fill.',
        })
      } catch (e) {
        return errorResult(e)
      }
    },
  })

  useMcpTool({
    name: 'list_broadcast_numbers',
    description:
      'List sending numbers eligible for a broadcast (verified / broadcast-ready), the same options the broadcast form offers. Use a returned number_id with set_broadcast_sender on the open broadcast form.',
    input: listBroadcastNumbersSchema,
    annotations: { readOnlyHint: true },
    handler: async () => {
      try {
        const { data } = await InboxesApi.getTeamsSearch({
          page: 1,
          per_page: 50,
          exclude_deprioritized: 1,
          by_all_members: true,
        })
        // Mirror the form's SMS filter: a toll-free number is only eligible when verified.
        const results = data.results.flatMap((inbox) =>
          (inbox.numbers ?? [])
            .filter((n) => !n.is_toll_free || n.verified_status === 'Verified')
            .map((n) => ({
              number_id: n.id,
              number: n.formatted_number || n.number,
              inbox: inbox.name,
              type: n.type,
              verified: n.verified_status === 'Verified',
            }))
        )
        return okResult({ total: results.length, results })
      } catch (e) {
        return errorResult(e)
      }
    },
  })

  return null
}
